import React, { useState } from 'react';
import { Text, Stack, Group, Button, Alert, Code } from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import UploadForm from '../../../components/upload/UploadForm';
import api from '../../../services/api';

/**
 * Admin version of UploadCSV page - works within AdminLayout (no Layout wrapper)
 */
export function AdminUploadCSV() {
  const [csvFile, setCsvFile] = useState<FileWithPath | null>(null);

  const handleFileDrop = (droppedFiles: FileWithPath[]) => {
    if (droppedFiles.length > 0) {
      const file = droppedFiles[0];
      if (file.name.toLowerCase().endsWith('.csv')) {
        setCsvFile(file);
      } else {
        notifications.show({
          title: 'Error',
          message: 'Please upload a CSV file',
          color: 'red',
        });
      }
    }
  };

  const removeFile = () => {
    setCsvFile(null);
  };

  const handleUpload = async (formData: FormData) => {
    return await api.post('/upload/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  };

  return (
    <UploadForm
      title="Upload Menu by CSV"
      uploadType="csv"
      fileFieldName="csvFile"
      files={csvFile ? [csvFile] : []}
      onFilesChange={(files) => setCsvFile(files[0] || null)}
      onUpload={handleUpload}
      noLayout={true}
    >
      {/* CSV-specific content */}
      <Alert title="CSV Format Requirements" color="blue" mb="md">
        <Text size="sm" mb="xs">
          Your CSV file must have the following headers (case-insensitive):
        </Text>
        <Code block>
          name,description,price,category,ingredients
        </Code>
        <Text size="sm" mt="xs">
          • <strong>name</strong>: Menu item name (required)<br/>
          • <strong>description</strong>: Item description (optional)<br/>
          • <strong>price</strong>: Price as number (required)<br/>
          • <strong>category</strong>: Category name (optional)<br/>
          • <strong>ingredients</strong>: Comma-separated ingredients (optional)
        </Text>
        <Text size="sm" mt="xs" c="dimmed">
          Note: Header names can be in any case (e.g., "Name", "NAME", "name" all work)
        </Text>
      </Alert>

      <Dropzone
        onDrop={handleFileDrop}
        accept={{
          'text/csv': ['.csv'],
        }}
        mb="md"
        maxFiles={1}
      >
        <Text ta="center" size="lg">
          Drag and drop a CSV file here, or click to select file
        </Text>
        <Text ta="center" size="sm" c="dimmed" mt={7}>
          Only CSV files are accepted
        </Text>
      </Dropzone>

      {csvFile && (
        <Stack mb="md">
          <Text fw={500}>CSV file to upload:</Text>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" style={{ wordBreak: 'break-all' }}>
              {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)
            </Text>
            <Button
              variant="subtle"
              color="red"
              size="xs"
              onClick={removeFile}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      )}
    </UploadForm>
  );
}






